import { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, Copy, Search, X } from 'lucide-react';
import AdminHeader from '../../components/AdminHeader';
import { PeriodSelector } from '../../components/AdminPeriodSelector';
import { useAdminPeriod } from '../../components/useAdminPeriod';
import { getMetaAdsUtmTemplate } from '../../lib/tracking/meta-ads';

const META_UTM_TEMPLATE = getMetaAdsUtmTemplate();

type MarketingRow = {
  origem: string;
  campanha: string;
  campanha_id: string;
  conjunto: string;
  conjunto_id: string;
  anuncio: string;
  anuncio_id: string;
  posicionamento: string;
  leads: number;
  pedidos: number;
  pagamentos: number;
  receita: number;
  ticket_medio: number | null;
  conversao: number | null;
};

type MarketingData = {
  totals: {
    leads: number;
    pedidos: number;
    pagamentos: number;
    receita: number;
    ticket_medio: number | null;
    conversao: number | null;
  };
  rows: MarketingRow[];
  filters: {
    campanhas: string[];
    anuncios: string[];
    origens: string[];
  };
};

// --- Comparação de criativos (section=criativos) ---
type AdOption = {
  ad_id: string;
  ad_name: string;
  campaign_name: string;
  spend: number;
  last_date: string;
};

type CompareAd = {
  ad_id: string;
  ad_name: string;
  campaign_name: string;
  adset_name: string;
  spend: number;
  impressions: number;
  clicks: number;
  link_clicks: number;
  video_plays: number;
  video_3s: number;
  thruplays: number;
  messaging_conversations: number;
  meta_purchases: number;
  hook_rate: number | null;
  ctr: number | null;
  link_ctr: number | null;
  cpc: number | null;
  cpc_link: number | null;
  custo_conversa: number | null;
  thruplay_rate: number | null;
  gerados: number;
  pagos: number;
  gmv: number;
  cpa: number | null;
  roas: number | null;
  ticket_medio: number | null;
};

type CompareData = {
  mode: 'compare';
  meta_error: string | null;
  video_degraded?: boolean;
  ads: CompareAd[];
};

const MAX_COMPARE = 6;

function formatBRL(value: number | null | undefined): string {
  const n = Number(value || 0);
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatMaybeBRL(value: number | null | undefined): string {
  if (value == null) return '—';
  return formatBRL(value);
}

function formatInt(value: number | null | undefined): string {
  return Number(value || 0).toLocaleString('pt-BR');
}

function formatPct(value: number | null | undefined): string {
  if (value == null) return '—';
  return `${Number(value).toFixed(1)}%`;
}

function sourceLabel(value: string): string {
  if (!value || value === 'sem_utm') return 'Sem UTM';
  if (value === 'whatsapp_ctwa') return 'WhatsApp CTWA';
  return value;
}

function formatRoas(value: number | null | undefined): string {
  if (value == null) return '—';
  return `${Number(value).toFixed(2)}x`;
}

// Linhas da tabela de comparação. `dir`: 'up' = maior melhor, 'down' = menor melhor,
// undefined = sem destaque de vencedor.
type CompareMetric = {
  label: string;
  hint?: string;
  get: (a: CompareAd) => number | null;
  fmt: (a: CompareAd) => string;
  dir?: 'up' | 'down';
};

const COMPARE_METRICS: CompareMetric[] = [
  { label: 'Investido', get: (a) => a.spend, fmt: (a) => formatBRL(a.spend) },
  { label: 'Impressões', get: (a) => a.impressions, fmt: (a) => formatInt(a.impressions) },
  { label: 'Hook rate', hint: 'views 3s ÷ impressões', get: (a) => a.hook_rate, fmt: (a) => formatPct(a.hook_rate), dir: 'up' },
  { label: 'Hold rate', hint: 'ThruPlays ÷ impressões', get: (a) => a.thruplay_rate, fmt: (a) => formatPct(a.thruplay_rate), dir: 'up' },
  { label: 'CTR', get: (a) => a.ctr, fmt: (a) => formatPct(a.ctr), dir: 'up' },
  { label: 'CTR (link)', get: (a) => a.link_ctr, fmt: (a) => formatPct(a.link_ctr), dir: 'up' },
  { label: 'CPC', get: (a) => a.cpc, fmt: (a) => formatMaybeBRL(a.cpc), dir: 'down' },
  { label: 'CPC (link)', get: (a) => a.cpc_link, fmt: (a) => formatMaybeBRL(a.cpc_link), dir: 'down' },
  { label: 'Conversas iniciadas', hint: 'reportado pelo Meta', get: (a) => a.messaging_conversations, fmt: (a) => formatInt(a.messaging_conversations), dir: 'up' },
  { label: 'Custo / conversa', get: (a) => a.custo_conversa, fmt: (a) => formatMaybeBRL(a.custo_conversa), dir: 'down' },
  { label: 'Pedidos gerados', hint: 'funil', get: (a) => a.gerados, fmt: (a) => formatInt(a.gerados), dir: 'up' },
  { label: 'Conversões pagas', hint: 'funil', get: (a) => a.pagos, fmt: (a) => formatInt(a.pagos), dir: 'up' },
  { label: 'CAC (custo / conversão)', get: (a) => a.cpa, fmt: (a) => formatMaybeBRL(a.cpa), dir: 'down' },
  { label: 'Receita (GMV)', get: (a) => a.gmv, fmt: (a) => formatBRL(a.gmv), dir: 'up' },
  { label: 'ROAS', get: (a) => a.roas, fmt: (a) => formatRoas(a.roas), dir: 'up' },
];

function compareErrorLabel(err: string | null): string | null {
  if (!err) return null;
  if (err === 'token_ausente') return 'Token Meta (ads_read) ausente ou expirado — mostrando só métricas de funil. Renove o token no kv_config.';
  return `Métricas do Meta indisponíveis (${err}) — mostrando só funil.`;
}

export default function Marketing() {
  const { apiQuery } = useAdminPeriod();
  const [data, setData] = useState<MarketingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [campaignFilter, setCampaignFilter] = useState('');
  const [adFilter, setAdFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [copied, setCopied] = useState(false);

  // Comparação de criativos
  const [adOptions, setAdOptions] = useState<AdOption[]>([]);
  const [selectedAds, setSelectedAds] = useState<string[]>([]);
  const [adSearch, setAdSearch] = useState('');
  const [compare, setCompare] = useState<CompareData | null>(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [compareError, setCompareError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await fetch(`/api/admin-dashboard?section=marketing&${apiQuery}`, {
        credentials: 'same-origin',
      });
      if (!res.ok) throw new Error(`status ${res.status}`);
      setData(await res.json());
    } catch (err) {
      setError(`Falha ao carregar: ${(err as Error).message}`);
    } finally {
      setLoading(false);
    }
  }, [apiQuery]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  // Lista de criativos pro seletor (vem do cache, sem chamar a Graph) — recarrega por período.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`/api/admin-dashboard?section=criativos&${apiQuery}`, { credentials: 'same-origin' });
        if (!res.ok) throw new Error(`status ${res.status}`);
        const j = await res.json();
        if (alive) setAdOptions(Array.isArray(j.ads) ? j.ads : []);
      } catch {
        if (alive) setAdOptions([]);
      }
    })();
    return () => { alive = false; };
  }, [apiQuery]);

  // Comparação ao vivo (Graph API) dos anúncios selecionados — recarrega por seleção/período.
  useEffect(() => {
    if (selectedAds.length === 0) {
      setCompare(null);
      setCompareError('');
      return;
    }
    let alive = true;
    setCompareLoading(true);
    setCompareError('');
    (async () => {
      try {
        const res = await fetch(
          `/api/admin-dashboard?section=criativos&ads=${selectedAds.join(',')}&${apiQuery}`,
          { credentials: 'same-origin' },
        );
        if (!res.ok) throw new Error(`status ${res.status}`);
        const j = (await res.json()) as CompareData;
        if (alive) setCompare(j);
      } catch (err) {
        if (alive) {
          setCompare(null);
          setCompareError(`Falha ao comparar: ${(err as Error).message}`);
        }
      } finally {
        if (alive) setCompareLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [selectedAds, apiQuery]);

  const toggleAd = useCallback((id: string) => {
    setSelectedAds((prev) =>
      prev.includes(id)
        ? prev.filter((x) => x !== id)
        : prev.length >= MAX_COMPARE
          ? prev
          : [...prev, id],
    );
  }, []);

  const visibleAdOptions = useMemo(() => {
    const q = adSearch.trim().toLowerCase();
    if (!q) return adOptions;
    return adOptions.filter(
      (o) => o.ad_name.toLowerCase().includes(q) || o.campaign_name.toLowerCase().includes(q),
    );
  }, [adOptions, adSearch]);

  const filteredRows = useMemo(() => {
    return (data?.rows ?? []).filter((row) => {
      if (campaignFilter && row.campanha !== campaignFilter) return false;
      if (adFilter && row.anuncio !== adFilter) return false;
      if (sourceFilter && row.origem !== sourceFilter) return false;
      if (statusFilter === 'com_pedido' && row.pedidos <= 0) return false;
      if (statusFilter === 'com_pagamento' && row.pagamentos <= 0) return false;
      return true;
    });
  }, [adFilter, campaignFilter, data, sourceFilter, statusFilter]);

  const totals = useMemo(() => {
    const total = filteredRows.reduce(
      (acc, row) => {
        acc.leads += row.leads;
        acc.pedidos += row.pedidos;
        acc.pagamentos += row.pagamentos;
        acc.receita += row.receita;
        return acc;
      },
      { leads: 0, pedidos: 0, pagamentos: 0, receita: 0 },
    );
    return {
      ...total,
      ticket_medio: total.pagamentos > 0 ? total.receita / total.pagamentos : null,
      conversao: total.pedidos > 0 ? (total.pagamentos / total.pedidos) * 100 : null,
    };
  }, [filteredRows]);

  const copyTemplate = async () => {
    await navigator.clipboard.writeText(META_UTM_TEMPLATE);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div
      className="admin-shell"
      style={{
        minHeight: '100vh',
        background: 'var(--cream)',
        color: 'var(--text-body)',
        fontFamily: "'DM Sans', sans-serif",
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <AdminHeader title="Marketing" />

      <main style={{ flex: 1, padding: '20px 16px 60px', maxWidth: 1320, margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, letterSpacing: '-0.03em', color: 'var(--heading-ink)' }}>
            Origem / UTM
          </h1>
          <PeriodSelector />
        </div>

        {loading && !data && <SkeletonGrid />}

        {error && (
          <Card>
            <p style={{ margin: 0, color: '#a93232', fontSize: 14 }}>{error}</p>
          </Card>
        )}

        {data && (
          <>
            <Card>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 520px', minWidth: 0 }}>
                  <h2 style={{ margin: '0 0 10px', fontSize: 15, fontWeight: 800, color: 'var(--heading-ink)' }}>
                    Configuração de UTMs
                  </h2>
                  <code
                    style={{
                      display: 'block',
                      padding: 12,
                      borderRadius: 8,
                      background: 'var(--badge-bg)',
                      border: '1px solid var(--border-soft)',
                      color: 'var(--text-strong)',
                      fontSize: 12,
                      lineHeight: 1.5,
                      overflowX: 'auto',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {META_UTM_TEMPLATE}
                  </code>
                  <p style={{ margin: '10px 0 0', fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                    Cole este conteúdo no campo “Parâmetros de URL” dos anúncios da Meta. Para rastreamento confiável,
                    mantenha os parâmetros de ID: campaign_id, adset_id e ad_id.
                  </p>
                </div>
                <button
                  onClick={copyTemplate}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    border: 'none',
                    borderRadius: 999,
                    background: 'var(--gold)',
                    color: '#fff',
                    padding: '10px 16px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                  {copied ? 'Copiado' : 'Copiar template'}
                </button>
              </div>
            </Card>

            <SectionTitle>Comparar criativos (Meta Ads)</SectionTitle>
            <Card>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 12,
                  flexWrap: 'wrap',
                  marginBottom: 12,
                }}
              >
                <div style={{ position: 'relative', flex: '1 1 280px', minWidth: 0 }}>
                  <Search
                    size={15}
                    style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }}
                  />
                  <input
                    value={adSearch}
                    onChange={(e) => setAdSearch(e.target.value)}
                    placeholder="Buscar anúncio pelo nome interno…"
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      padding: '9px 12px 9px 32px',
                      borderRadius: 8,
                      border: '1px solid var(--border-soft)',
                      background: 'var(--surface-white)',
                      color: 'var(--text-strong)',
                      fontFamily: 'inherit',
                      fontSize: 13,
                    }}
                  />
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                  {selectedAds.length}/{MAX_COMPARE} selecionados
                  {selectedAds.length > 0 && (
                    <button
                      onClick={() => setSelectedAds([])}
                      style={{
                        marginLeft: 10,
                        border: 'none',
                        background: 'transparent',
                        color: 'var(--gold)',
                        fontWeight: 700,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        fontSize: 12,
                      }}
                    >
                      Limpar
                    </button>
                  )}
                </div>
              </div>

              <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid var(--border-soft)', borderRadius: 10 }}>
                {visibleAdOptions.length === 0 ? (
                  <p style={{ margin: 0, padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                    Nenhum criativo com dados no período.
                  </p>
                ) : (
                  visibleAdOptions.map((o) => {
                    const checked = selectedAds.includes(o.ad_id);
                    const disabled = !checked && selectedAds.length >= MAX_COMPARE;
                    return (
                      <label
                        key={o.ad_id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          padding: '8px 12px',
                          borderTop: '1px solid var(--border-fade)',
                          cursor: disabled ? 'not-allowed' : 'pointer',
                          opacity: disabled ? 0.5 : 1,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={disabled}
                          onChange={() => toggleAd(o.ad_id)}
                          style={{ accentColor: 'var(--gold)', width: 16, height: 16, flexShrink: 0 }}
                        />
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span
                            style={{ display: 'block', fontWeight: 600, fontSize: 13, color: 'var(--text-strong)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            title={o.ad_name}
                          >
                            {o.ad_name}
                          </span>
                          <span
                            style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            title={o.campaign_name}
                          >
                            {o.campaign_name || '—'}
                          </span>
                        </span>
                        <span style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{formatBRL(o.spend)}</span>
                      </label>
                    );
                  })
                )}
              </div>

              {compareError && <p style={{ margin: '12px 0 0', color: '#a93232', fontSize: 13 }}>{compareError}</p>}
              {compare && compareErrorLabel(compare.meta_error) && (
                <p
                  style={{
                    margin: '12px 0 0',
                    padding: '10px 12px',
                    borderRadius: 8,
                    background: 'var(--gold-pale)',
                    border: '1px solid var(--gold-border)',
                    color: 'var(--amber-deep)',
                    fontSize: 12.5,
                    lineHeight: 1.5,
                  }}
                >
                  {compareErrorLabel(compare.meta_error)}
                </p>
              )}
              {compare && !compare.meta_error && compare.video_degraded && (
                <p style={{ margin: '12px 0 0', fontSize: 11.5, color: 'var(--text-muted)' }}>
                  Hook/hold rate aproximados — métrica de vídeo de 3s indisponível na conta; usando “video plays”.
                </p>
              )}
              <p style={{ margin: '12px 0 0', fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                Selecione 2+ anúncios pra comparar lado a lado. Métricas de Meta (hook rate, CTR, CPC, custo/conversa)
                vêm <strong>ao vivo da API do Meta Ads</strong> no período selecionado; pedidos/conversões/ROAS vêm do funil.
              </p>
            </Card>

            {compareLoading && !compare && (
              <Card>
                <p style={{ margin: 0, padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                  Carregando métricas ao vivo do Meta…
                </p>
              </Card>
            )}
            {compare && compare.ads.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <ComparisonTable ads={compare.ads} onRemove={toggleAd} />
              </div>
            )}

            <CardGrid mt={16}>
              <KpiCard label="Leads/conversas" value={formatInt(totals.leads)} />
              <KpiCard label="Pedidos criados" value={formatInt(totals.pedidos)} />
              <KpiCard label="Pagamentos" value={formatInt(totals.pagamentos)} />
              <KpiCard label="Receita" value={formatBRL(totals.receita)} accentGold />
              <KpiCard label="Ticket médio" value={formatMaybeBRL(totals.ticket_medio)} />
              <KpiCard label="Pedido → pagamento" value={formatPct(totals.conversao)} />
            </CardGrid>

            <SectionTitle>Filtros</SectionTitle>
            <Card>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                <Select label="Origem" value={sourceFilter} onChange={setSourceFilter} options={data.filters.origens} labelFn={sourceLabel} />
                <Select label="Campanha" value={campaignFilter} onChange={setCampaignFilter} options={data.filters.campanhas} />
                <Select label="Anúncio" value={adFilter} onChange={setAdFilter} options={data.filters.anuncios} />
                <Select
                  label="Status"
                  value={statusFilter}
                  onChange={setStatusFilter}
                  options={['todos', 'com_pedido', 'com_pagamento']}
                  labelFn={(v) => ({ todos: 'Todos', com_pedido: 'Com pedido', com_pagamento: 'Com pagamento' }[v] || v)}
                  includeAll={false}
                />
              </div>
            </Card>

            <SectionTitle>Performance por campanha/anúncio</SectionTitle>
            <Card>
              {filteredRows.length === 0 ? (
                <EmptyMsg>Nenhuma origem no período.</EmptyMsg>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', minWidth: 1180, borderCollapse: 'collapse', fontSize: 12.5 }}>
                    <thead>
                      <tr style={{ color: 'var(--text-muted)', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 0.06 }}>
                        {['Origem', 'Campanha', 'Campanha ID', 'Conjunto', 'Conjunto ID', 'Anúncio', 'Anúncio ID', 'Posicionamento', 'Leads', 'Pedidos', 'Pagos', 'Receita', 'Ticket', 'Conversão'].map((h) => (
                          <th key={h} style={{ textAlign: ['Leads', 'Pedidos', 'Pagos', 'Receita', 'Ticket', 'Conversão'].includes(h) ? 'right' : 'left', padding: '7px 8px', whiteSpace: 'nowrap' }}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRows.map((row) => (
                        <tr key={`${row.origem}:${row.campanha_id}:${row.conjunto_id}:${row.anuncio_id}:${row.posicionamento}`} style={{ borderTop: '1px solid var(--border-fade)' }}>
                          <TextCell value={sourceLabel(row.origem)} strong />
                          <TextCell value={row.campanha || '—'} />
                          <TextCell value={row.campanha_id || '—'} mono />
                          <TextCell value={row.conjunto || '—'} />
                          <TextCell value={row.conjunto_id || '—'} mono />
                          <TextCell value={row.anuncio || '—'} strong />
                          <TextCell value={row.anuncio_id || '—'} mono />
                          <TextCell value={row.posicionamento || '—'} />
                          <MetricCell value={formatInt(row.leads)} />
                          <MetricCell value={formatInt(row.pedidos)} />
                          <MetricCell value={formatInt(row.pagamentos)} />
                          <MetricCell value={formatBRL(row.receita)} strong />
                          <MetricCell value={formatMaybeBRL(row.ticket_medio)} />
                          <MetricCell value={formatPct(row.conversao)} />
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </>
        )}
      </main>
    </div>
  );
}

function Select({
  label,
  value,
  onChange,
  options,
  labelFn = (v) => v,
  includeAll = true,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: string[];
  labelFn?: (value: string) => string;
  includeAll?: boolean;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
      {label}
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        style={{
          borderRadius: 8,
          border: '1px solid var(--border-soft)',
          background: 'var(--surface-white)',
          color: 'var(--text-strong)',
          padding: '9px 10px',
          fontFamily: 'inherit',
          fontSize: 13,
          fontWeight: 600,
          minWidth: 0,
        }}
      >
        {includeAll && <option value="">Todos</option>}
        {options.map((option) => (
          <option key={option} value={option}>{labelFn(option)}</option>
        ))}
      </select>
    </label>
  );
}

function TextCell({ value, strong, mono }: { value: string; strong?: boolean; mono?: boolean }) {
  return (
    <td
      title={value}
      style={{
        padding: '8px',
        color: strong ? 'var(--heading-ink)' : 'var(--text-strong)',
        fontWeight: strong ? 700 : 500,
        fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : undefined,
        maxWidth: 170,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {value}
    </td>
  );
}

function MetricCell({ value, strong }: { value: string; strong?: boolean }) {
  return (
    <td style={{ padding: '8px', textAlign: 'right', fontWeight: strong ? 800 : 600, color: strong ? 'var(--heading-ink)' : 'var(--text-strong)', whiteSpace: 'nowrap' }}>
      {value}
    </td>
  );
}

function CardGrid({ children, mt = 0 }: { children: React.ReactNode; mt?: number }) {
  return <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginTop: mt }}>{children}</div>;
}

function Card({ children }: { children: React.ReactNode }) {
  return <div className="admin-glass" style={{ background: 'var(--surface-white)', borderRadius: 18, padding: 16 }}>{children}</div>;
}

function ComparisonTable({ ads, onRemove }: { ads: CompareAd[]; onRemove: (id: string) => void }) {
  // Melhor valor da linha (verde) — só com 2+ anúncios e métrica direcional.
  const bestOf = (m: CompareMetric): number | null => {
    if (!m.dir || ads.length < 2) return null;
    const vals = ads.map((a) => m.get(a)).filter((v): v is number => v != null);
    if (vals.length === 0) return null;
    return m.dir === 'up' ? Math.max(...vals) : Math.min(...vals);
  };

  return (
    <Card>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 200 + ads.length * 150 }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '8px 10px', position: 'sticky', left: 0, background: 'var(--surface-white)', minWidth: 150, zIndex: 1 }} />
              {ads.map((a) => (
                <th key={a.ad_id} style={{ textAlign: 'right', padding: '8px 10px', verticalAlign: 'bottom', minWidth: 140 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                    <button
                      onClick={() => onRemove(a.ad_id)}
                      title="Remover da comparação"
                      style={{ border: 'none', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', padding: 0, marginBottom: 2, lineHeight: 0 }}
                    >
                      <X size={13} />
                    </button>
                    <span
                      style={{ fontWeight: 800, fontSize: 12.5, color: 'var(--heading-ink)', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={a.ad_name}
                    >
                      {a.ad_name}
                    </span>
                    <span
                      style={{ fontSize: 10.5, fontWeight: 500, color: 'var(--text-muted)', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={a.campaign_name}
                    >
                      {a.campaign_name || '—'}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {COMPARE_METRICS.map((m) => {
              const best = bestOf(m);
              return (
                <tr key={m.label} style={{ borderTop: '1px solid var(--border-fade)' }}>
                  <td style={{ padding: '8px 10px', position: 'sticky', left: 0, background: 'var(--surface-white)', zIndex: 1 }}>
                    <span style={{ fontWeight: 600, color: 'var(--text-strong)' }}>{m.label}</span>
                    {m.hint && <span style={{ display: 'block', fontSize: 10.5, color: 'var(--text-muted)' }}>{m.hint}</span>}
                  </td>
                  {ads.map((a) => {
                    const v = m.get(a);
                    const isBest = best != null && v != null && v === best;
                    return (
                      <td
                        key={a.ad_id}
                        style={{
                          padding: '8px 10px',
                          textAlign: 'right',
                          fontWeight: isBest ? 800 : 600,
                          color: isBest ? '#2f7a4a' : 'var(--text-strong)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {m.fmt(a)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h2 style={{ fontSize: 14, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--heading-ink)', margin: '24px 0 8px' }}>{children}</h2>;
}

function EmptyMsg({ children }: { children: React.ReactNode }) {
  return <p style={{ margin: 0, padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>{children}</p>;
}

function KpiCard({ label, value, accentGold }: { label: string; value: string; accentGold?: boolean }) {
  return (
    <div
      className={accentGold ? 'admin-glass admin-accent-gold' : 'admin-glass'}
      style={{ background: 'var(--surface-white)', borderRadius: 18, padding: '17px 19px', minHeight: 88 }}
    >
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </div>
      <div style={{ fontSize: 27, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--heading-ink)', marginTop: 5, lineHeight: 1.1 }}>
        {value}
      </div>
    </div>
  );
}

function SkeletonGrid() {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12 }}>
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div key={i} style={{ height: 88, background: 'var(--badge-bg)', borderRadius: 18, border: '1px solid var(--border-soft)', opacity: 0.6 }} />
      ))}
    </div>
  );
}
